import React, { createContext, useCallback, useEffect, useReducer } from 'react'

import { AuthError, fetchAuthSession, fetchUserAttributes, signIn, signOut } from 'aws-amplify/auth'
import { AxiosError } from 'axios'
import { t } from 'i18next'

const INITIALIZE = 'INITIALIZE'
const SIGN_OUT = 'SIGN_OUT'

const initialState: CognitoState = {
    isAuthenticated: false,
    isInitialized: false,
    isSuperadmin: false,
    user: null,
    method: 'cognito',
    signIn: () => {
        throw new Error('Not initiated yet.')
    },
    resetPassword: () => {
        throw new Error('Not initiated yet.')
    },
    signOut: () => {
        throw new Error('Not initiated yet.')
    },
}

type CognitoStateUser = {
    displayName: string
    groups?: Array<'superadmin'>
}

export interface CognitoState {
    isAuthenticated: boolean
    isSuperadmin: boolean
    isInitialized: boolean
    user: CognitoStateUser | null
    method: 'cognito'
    signIn: (email: string, pwd: string) => Promise<{ newPasswordRequired: boolean }>
    resetPassword: (email: string) => Promise<{ success: boolean; message?: string }>
    signOut: () => void
}

const reducer = (state: CognitoState, action: { payload?: Partial<CognitoState>; type: string }) => {
    if (action.type === INITIALIZE) {
        const { isAuthenticated, user } = action.payload as Pick<CognitoState, 'isAuthenticated' | 'user'>
        return {
            ...state,
            isAuthenticated,
            isSuperadmin: user?.groups?.includes('superadmin') ?? false,
            isInitialized: true,
            user,
        }
    }
    if (action.type === SIGN_OUT) {
        return {
            ...state,
            isAuthenticated: false,
            user: null,
        }
    }
    return state
}

const AuthContext = createContext<CognitoState | null>(null)

function AuthProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initialState)

    const getSession = useCallback(async () => {
        const authSession = await fetchAuthSession()
        const userAttributes = await fetchUserAttributes()

        const { tokens } = authSession

        if (!tokens) {
            throw new Error('Failed to read session tokens')
        }

        dispatch({
            type: INITIALIZE,
            payload: {
                isAuthenticated: true,
                user: {
                    ...userAttributes,
                    displayName: userAttributes.name ?? 'Undefined',
                    groups: tokens.accessToken.payload['cognito:groups'] as Array<'superadmin'>,
                },
            },
        })
    }, [])

    const initialize = useCallback(async () => {
        try {
            await getSession()
        } catch {
            dispatch({
                type: INITIALIZE,
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            })
        }
    }, [getSession])

    useEffect(() => {
        void initialize()
    }, [initialize])

    const signInOwn = useCallback(
        async (email: string, password: string) => {
            try {
                const response = await signIn({ username: email, password })

                if (response.nextStep.signInStep) {
                    switch (response.nextStep.signInStep) {
                        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                            return { newPasswordRequired: true }
                    }
                }

                if (response.isSignedIn) {
                    await getSession()
                    return { newPasswordRequired: false }
                }
            } catch (err) {
                let userMessage: string | null = null
                if (err instanceof AuthError) {
                    switch (err.name) {
                        // https://github.com/aws-amplify/amplify-js/issues/9104
                        case 'UserAlreadyAuthenticatedException': {
                            await signOutSelf()

                            return signInOwn(email, password)
                        }
                        case 'UserNotFoundException':
                        case 'NotAuthorizedException': {
                            if (err.message.includes('is disabled')) {
                                userMessage = t('pages.auth.signIn.disabledUser')
                            }
                            if (
                                err.name === 'UserNotFoundException' ||
                                err.message === 'Incorrect username or password.'
                            ) {
                                userMessage = t('pages.auth.signIn.incorrectUsernameOrPassword')
                            }
                            break
                        }
                    }
                }

                console.error(err)
                throw new Error(userMessage ?? t('errors.unexpected'))
            }

            return { newPasswordRequired: false }
        },
        [getSession],
    )

    const signOutSelf = async () => {
        await signOut()
        dispatch({ type: SIGN_OUT })
    }

    const resetPassword = async (email: string) => {
        try {
            const { default: UserApi } = await import('../api/UserApi')
            const userApi = new UserApi()
            // Call the API to request a password reset code
            await userApi.requestPasswordResetCode(email)
            return { success: true }
        } catch (error) {
            const msg =
                error instanceof AxiosError
                    ? (error.response?.data.message as string)
                    : 'Failed to request password reset code'
            return { success: false, message: msg }
        }
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'cognito',
                user: {
                    displayName: state?.user?.displayName || 'Undefined',
                    ...state.user,
                },
                signIn: signInOwn,
                signOut: signOutSelf,
                resetPassword,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export { AuthContext, AuthProvider }
